/**
 * 이상한 방 - 순위표 (여럿이 결과 화면, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.5절(점수) · 8절("한 판 더 하자는 말이 나오는가")
 *            로드맵 M2(`ResultView.ranking`이 있을 때 결과 화면이 끼우는 순위 목록 - 라운드 점수·누적·등수·내 줄 강조)
 *
 * ## 결과 화면이 그리지 않고 여기가 그린다
 *
 * `resultScreen.ts`는 순위 **자리**(`.rs-ranking`)만 편다. 줄을 그리는 손이 하나여야 표의 모양이 한 벌이다 - 결과 화면이
 * 자기 줄을 만들기 시작하면 다음에 순위표가 필요한 화면(로비의 지난 판, M6)이 같은 표를 두 벌째로 그린다. 그래서 이 파일은
 * `root`를 내주고, 어디에 끼울지는 부르는 쪽이 정한다(핀 칩과 같은 결정 - `pinsChip.ts` 머리말).
 *
 * ## 계산하지 않는다
 *
 * 등수·점수·누적은 호스트가 굳힌 값이다(`matchScore.cumulativeRanking` - 같은 점수는 같은 등수). 여기서 정렬하거나 등수를
 * 다시 매기면 호스트의 표와 화면의 표가 갈린다. 받은 순서 그대로, 받은 숫자 그대로 그린다. 이름도 받는다 - id는 화면에
 * 나오지 않고(`matchSessionView.rankingRows`), 사람 이름은 번역 대상이 아니다.
 *
 * ## 예외 하나 - 내 별명
 *
 * 별명 한 줄만은 뷰에 없는 값을 읽는다(`ui/cosmeticVars.myNicknameLabel()`). 남의 사물함은 뷰에 오지 않으므로
 * (호스트가 뷰를 만들고 호스트는 계정 저장을 모른다 - `matchView.ts`) 별명은 **내 줄에만** 붙고, 그 값의 출처는
 * 모듈 수준 상태다. 선례는 `ui/settings.isSettingsOpen()`이다. 같은 입력으로 두 번 그려도 결과가 같지만,
 * `applyCosmetics`를 부르기 전과 후는 다르다는 뜻이다.
 *
 * ## 라운드당 한 번이라 매번 다시 만든다
 *
 * 프레임마다 도는 참가자 줄(`playerStrip.ts`)과 달리 이 표는 결과가 뜰 때 한 번 그려진다. 줄을 재사용해 아끼는 비용보다
 * "상태가 갱신되지 않은 줄이 하나 남는" 버그의 비용이 훨씬 크다(`levelSelect.ts`의 같은 결정). 언어가 바뀌면 결과 화면이
 * 같은 뷰로 `render`를 다시 부르므로 머리 줄의 문구도 그때 새 언어가 된다.
 */

import { t } from '../i18n'

import { myNicknameLabel } from './cosmeticVars'
import { createElement, formatNumber } from './dom'

import './rankingList.css'

/* --- 타입 ---------------------------------------------------------------------- */

/**
 * 한 줄. `resultScreen.ResultRankingRow`와 같은 모양이라 그 배열을 그대로 넘기면 된다(구조적 타이핑).
 * 그 타입을 import 하지 않는 이유는 결과 화면이 이 파일을 import 하기 때문이다 - 서로를 가져오면 순환이다.
 */
export type RankingListRow = Readonly<{
  name: string
  /** 이번 라운드 순위. 같은 점수는 같은 순위다(호스트가 매긴 값 그대로). */
  rank: number
  roundScore: number
  totalScore: number
  /** 내 줄인가. 이 줄만 다르게 그린다. */
  isMe: boolean
  /**
   * **내 구획에서 아무도 못 찾은 수**(여럿이 M11). 그 수 × 200이 이 줄의 점수 절반을 만드는 자리라, 순위표가
   * "왜 저 사람이 1등인가"를 한 줄로 답한다.
   *
   * **내 줄에만 온다**(선택 필드). 남의 미발견 수는 뷰에 없다 - `SelfView.unfoundCount`가 본인에게만 가는 값이고
   * (`matchView.ts`), 여기서 정답표를 걸러 세면 점수의 분자가 화면에 두 벌째로 생긴다. 없으면 줄이 서지 않는다.
   */
  zoneUnfound?: number
}>

export type RankingListHandle = Readonly<{
  root: HTMLElement
  /** 표를 그린다. 빈 배열이면 줄 없이 머리만 남는다 - 비어 있는 표를 세우지 않는 것은 부르는 쪽의 몫이다(`ResultView.ranking`이 없으면 자리째 접힌다). */
  render: (rows: readonly RankingListRow[]) => void
  dispose: () => void
}>

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 순위표를 만든다. 부모에 붙이는 것은 호출자의 몫이다(`root`를 내준다).
 */
export function createRankingList(): RankingListHandle {
  const root = createElement('section', 'rk-root')
  root.setAttribute('aria-labelledby', 'rk-title')

  const head = createElement('div', 'rk-head')
  const title = createElement('h2', 'rk-title', '')
  title.id = 'rk-title'
  const roundCol = createElement('span', 'rk-col', '')
  const totalCol = createElement('span', 'rk-col', '')
  head.append(title, roundCol, totalCol)

  const list = createElement('ol', 'rk-list')
  root.append(head, list)

  function createRow(row: RankingListRow): HTMLLIElement {
    const item = createElement('li', row.isMe ? 'rk-row is-me' : 'rk-row')
    const rowAria = t('ranking.rowAria', {
      rank: row.rank,
      name: row.name,
      round: formatNumber(row.roundScore),
      total: formatNumber(row.totalScore),
    })
    // 내 구획 미발견은 이 줄의 점수를 설명하는 값이라 같은 문장에 이어 붙는다(목록을 잇는 자리는 `common.separator`다).
    item.setAttribute(
      'aria-label',
      row.zoneUnfound === undefined
        ? rowAria
        : `${rowAria}${t('common.separator')}${t('zone.result.rowAria', { count: row.zoneUnfound })}`,
    )
    // 등수는 글자다(`{rank}등` / `#{rank}`). 1등에만 색을 주면 2등부터는 표에서 자기를 못 찾는다.
    const rank = createElement('span', 'rk-row__rank', t('ranking.rank', { rank: row.rank }))
    rank.setAttribute('aria-hidden', 'true')
    const name = createElement('span', 'rk-row__name')
    name.setAttribute('aria-hidden', 'true')
    /*
     * 이름은 **자기 상자**를 갖는다. 전에는 `.rk-row__name`의 직접 텍스트였는데, 그 상자는 세로로 쌓기 위한
     * flex 컨테이너라 거기 걸린 `text-overflow: ellipsis`가 아무 일도 하지 않았다(말줄임은 블록 컨테이너의
     * 성질이다) - 긴 이름이 말줄임 없이 그냥 하드컷됐다. 자르는 성질은 실제로 자르는 상자에 둔다.
     */
    const who = createElement('span', 'rk-row__who', row.name)
    /*
     * 별명(로드맵 M9). 내 줄에만 온다 - 남의 사물함은 호스트도 모른다(`ui/cosmeticVars.ts` 머리말).
     * 미발견 줄과 같은 자리 규칙이라 이름 아래로 들어간다.
     */
    name.append(who)
    const nickname = row.isMe ? myNicknameLabel() : null
    if (nickname !== null) {
      const nick = createElement('span', 'rk-row__nick', nickname)
      nick.setAttribute('aria-hidden', 'true')
      name.append(nick)
    }
    // 내 구획 미발견(M11). 이름 아래 작은 한 줄 - 열을 하나 더 만들면 남의 줄에는 영원히 빈 칸이 선다.
    if (row.zoneUnfound !== undefined) {
      const note = createElement('span', 'rk-row__zone', t('zone.result.rowAria', { count: row.zoneUnfound }))
      note.setAttribute('aria-hidden', 'true')
      name.append(note)
    }
    const round = createElement('span', 'rk-row__round', formatNumber(row.roundScore))
    round.setAttribute('aria-hidden', 'true')
    const total = createElement('span', 'rk-row__total', formatNumber(row.totalScore))
    total.setAttribute('aria-hidden', 'true')
    item.append(rank, name, round, total)
    return item
  }

  return {
    root,

    render(rows: readonly RankingListRow[]): void {
      // 문구는 매번 다시 굽는다 - 언어 전환 뒤의 `render`가 옛 언어를 남기지 않게.
      title.textContent = t('ranking.title')
      roundCol.textContent = t('ranking.round')
      totalCol.textContent = t('ranking.total')
      list.replaceChildren(...rows.map(createRow))
    },

    dispose(): void {
      root.remove()
    },
  }
}
